import { activeClinicaId } from "@/lib/api";

export type CampaignChannel = "email" | "whatsapp";
export type CampaignType = "operacional" | "marketing";
export type CampaignSegment = "todos_ativos" | "aniversariantes" | "sem_consulta";

export type CommunicationSettings = {
  comunicacoes_ativas: boolean;
  email_ativo: boolean;
  whatsapp_ativo: boolean;
  campanhas_ativas: boolean;
  transacionais_ativas: boolean;
  lembretes_ativos: boolean;
  janela_inicio: string;
  janela_fim: string;
  limite_hora: number;
  limite_whatsapp_hora: number;
  max_whatsapp_lote: number;
  intervalo_whatsapp_segundos: number;
  cooldown_marketing_dias: number;
  pausar_apos_erros_whatsapp: number;
  smtp_host: string;
  smtp_port: number;
  smtp_security: "starttls" | "ssl" | "none";
  smtp_username: string;
  smtp_password: string;
  smtp_from_email: string;
  smtp_from_name: string;
  smtp_configured?: boolean;
  eventos: Record<string, boolean>;
};

export type CampaignClinic = { id: string; nome: string; nome_exibido?: string; nome_fantasia?: string };
export type Campaign = {
  id: string;
  nome: string;
  tipo_campanha: CampaignType;
  segmento: CampaignSegment;
  clinica_ids: string[];
  canais: CampaignChannel[];
  programado_para: string;
  modo_envio?: "agora" | "agendado";
  status: "Agendada" | "Pausada" | "Cancelada" | "Concluída";
  total_destinatarios: number;
  criado_por_nome?: string;
  resumo?: CampaignPreview;
  created_at?: string;
};

export type CampaignPreview = {
  pacientes_analisados: number;
  destinatarios: number;
  sem_consentimento: number;
  cooldown_whatsapp: number;
  sem_contato: number;
  duplicados: number;
  por_unidade: Record<string, number>;
};

type CampaignResponse = { ok: boolean; error?: string; [key: string]: unknown };

async function campaignRequest<T extends CampaignResponse>(action: string, body?: Record<string, unknown>): Promise<T> {
  const params = new URLSearchParams({ action });
  const clinicId = activeClinicaId();
  if (clinicId) params.set("clinica_id", clinicId);
  const response = await fetch(`/api/campanhas.php?${params.toString()}`, {
    method: body ? "POST" : "GET",
    credentials: "include",
    cache: "no-store",
    headers: body ? { "Content-Type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const raw = await response.text();
  let payload: T;
  try {
    payload = (raw ? JSON.parse(raw) : {}) as T;
  } catch {
    const requestRef = response.headers.get("x-request-id");
    const detail = response.status ? ` (HTTP ${response.status}${requestRef ? `, ref. ${requestRef}` : ""})` : "";
    throw new Error(`A Central de Comunicação retornou uma resposta inválida${detail}. Tente novamente.`);
  }
  if (!response.ok || payload.error) throw new Error(payload.error || `Erro na Central de Comunicação (HTTP ${response.status})`);
  return payload;
}

export const campaignApi = {
  bootstrap: () => campaignRequest<CampaignResponse & { clinicas: CampaignClinic[]; campanhas: Campaign[]; configuracoes: Record<string, CommunicationSettings>; pode_rede: boolean }>("bootstrap"),
  preview: (body: Record<string, unknown>) => campaignRequest<CampaignResponse & { resumo: CampaignPreview; amostra: Array<{ paciente_nome: string; clinica_nome: string; canal: CampaignChannel; destino: string }> }>("preview", body),
  create: (body: Record<string, unknown>) => campaignRequest<CampaignResponse & { campanha: Campaign }>("create", body),
  setStatus: (id: string, status: "Pausada" | "Agendada" | "Cancelada") => campaignRequest("status", { id, status }),
  saveSettings: (clinicIds: string[], configuracao: Partial<CommunicationSettings>) => campaignRequest("save_settings", { clinica_ids: clinicIds, configuracao }),
  suggestText: (objetivo: string, tipo_campanha: CampaignType) => campaignRequest<CampaignResponse & { mensagem: string }>("suggest_text", { objetivo, tipo_campanha }),
};

export async function queueCommunicationEvent(input: Record<string, unknown>) {
  return campaignRequest<CampaignResponse & { enfileirados?: number; ignorado?: string }>("event", input);
}

export type DocumentChannel = "email" | "whatsapp";

export async function sendPatientDocument(input: {
  entidade: string;
  documento_id: string;
  clinica_id?: string;
  canais: DocumentChannel[];
  request_id: string;
}) {
  return campaignRequest<CampaignResponse & {
    enfileirados: number;
    canais: DocumentChannel[];
    fila_ids?: string[];
    envio_imediato?: { ok?: boolean; ocupado?: boolean; processados?: number; enviados?: number; erros?: number; motivo?: string; detalhes?: Array<{ canal?: string; status?: string; erro?: string }> };
    ignorados?: Partial<Record<DocumentChannel, string>>;
    duplicado?: boolean;
    validade?: string;
  }>("send_document", input);
}
